import cs from 'classnames';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col } from '../col';
import { Link } from '../link';
import { Progress, type ProgressProperties } from '../progress';
import { Row } from '../row';
import { Tag, type TagProperties } from '../tag';
import { Text } from '../typography';
import { DataCardDescription, type DataCardDescriptionProperties } from './data-card-description';
import { DataCardParticipants, type ParticipantsProperties } from './data-card-participants';
import { DataCardPrice, type PricesProperties } from './data-card-price';
import { ThumbnailImage } from './thumbnail-image';

type CardProgressProperties = {
  color?: ProgressProperties['color'];
  max: number;
  size?: ProgressProperties['size'];
  value: number;
};

type P = {
  className?: string;
  description?: DataCardDescriptionProperties;
  image: { action?: ReactNode; alt?: string; height?: number; meta?: ReactNode; src: string };
  participantsProperties?: ParticipantsProperties;
  path: string;
  pricesProperties?: PricesProperties;
  progressProperties?: CardProgressProperties;
  tag?: TagProperties | TagProperties[];
  title: string;
};

const DataCard = ({
  className,
  description,
  image,
  participantsProperties,
  path,
  pricesProperties,
  progressProperties,
  tag,
  title,
}: P) => {
  const { t } = useTranslation();
  const percent = useMemo(
    () => (progressProperties ? (progressProperties.value / progressProperties.max) * 100 : 0),
    [progressProperties],
  );

  // eslint-disable-next-line sonarjs/no-nested-conditional -- TODO: fix later
  const tagItems = tag ? (Array.isArray(tag) ? tag : [tag]) : null;

  return (
    <Link className={cs('data-card', className)} effects={{ focus: true }} to={path}>
      <Row>
        <Col className="data-card__cover-photo" span={24}>
          {/* TODO: Image behavior and properties will be fixed along with 16:9 update for the card */}
          <ThumbnailImage
            action={image.action}
            image={{
              alt: image.alt,
              className: 'data-card__cover-photo__image',
              height: image.height ?? 160,
              preview: false,
              src: image.src,
            }}
            meta={image.meta}
          />
        </Col>
        <Col span={24}>
          {tagItems && tagItems.length > 0 ? (
            <Row gutter={['xs', 0]}>
              {tagItems.map((tagItem, index) => (
                // eslint-disable-next-line react/no-array-index-key -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                <Col key={index}>
                  <Tag {...tagItem}>{tagItem.children}</Tag>
                </Col>
              ))}
            </Row>
          ) : null}
        </Col>
        <Col span={24}>
          <Text type="title" weight="bold" withinLink={{ hasHoverEffect: true }}>
            {t(title)}
          </Text>
        </Col>
        {description ? (
          <Col span={24}>
            <DataCardDescription description={description} />
          </Col>
        ) : null}
        {participantsProperties ? (
          <Col span={24}>
            <DataCardParticipants participants={participantsProperties} />
          </Col>
        ) : null}
        {progressProperties ? (
          <Col span={24}>
            <Progress
              color={progressProperties.color}
              percent={percent}
              showInfo={false}
              size={progressProperties.size}
            />
          </Col>
        ) : null}
        {pricesProperties ? (
          <Col span={24}>
            <DataCardPrice prices={pricesProperties} />
          </Col>
        ) : null}
      </Row>
    </Link>
  );
};

export default DataCard;
